import { useState, useEffect } from 'react';
import { Loader2, Calendar, Plus, Trash2, Target, ArrowLeft } from 'lucide-react';
import { Button } from '@travellegacy/nexops-design-system';
import { Input } from '@travellegacy/nexops-design-system';
import { Textarea } from '@travellegacy/nexops-design-system';
import { Select } from '@travellegacy/nexops-design-system';
import { MoodSelector } from './MoodSelector';
import { Card, CardContent, CardHeader, CardTitle } from '@travellegacy/nexops-design-system';
import { Badge } from '@travellegacy/nexops-design-system';
import { useCreateCheckIn } from '../hooks/useCheckIns';
import { usePDIs } from '../hooks/usePDIs';
import { useEmployees } from '@/features/people-core/hooks/useEmployees';
import type { CreateCheckInInput } from '../types';

interface CreateCheckInFormProps {
  onCancel: () => void;
  onSuccess?: () => void;
}

interface AgreementForm {
  description: string;
}

const emptyAgreement: AgreementForm = {
  description: '',
};

/**
 * Formulário para criar novo Check-in mensal
 */
export function CreateCheckInForm({ onCancel, onSuccess }: CreateCheckInFormProps) {
  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear();

  const [employeeId, setEmployeeId] = useState('');
  const [date, setDate] = useState(now.toISOString().split('T')[0]);
  const month = currentMonth;
  const year = currentYear;
  const [moodScore, setMoodScore] = useState<number>(3);
  const [highlights, setHighlights] = useState('');
  const [concerns, setConcerns] = useState('');
  const [supportNeeds, setSupportNeeds] = useState('');
  const [pdiProgress, setPdiProgress] = useState<number>(3);
  const [observations, setObservations] = useState('');
  const [agreements, setAgreements] = useState<AgreementForm[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const createCheckIn = useCreateCheckIn();
  const { data: employees } = useEmployees();

  // Buscar PDI ativo do colaborador selecionado
  const { data: pdiData } = usePDIs({
    employeeId: employeeId || undefined,
    status: 'ACTIVE',
  });

  const activePDI = pdiData?.pdis?.[0];

  // Resetar acordos quando mudar de colaborador
  useEffect(() => {
    setAgreements([]);
  }, [employeeId]);

  const handleAddAgreement = () => {
    setAgreements([...agreements, { ...emptyAgreement }]);
  };

  const handleRemoveAgreement = (index: number) => {
    setAgreements(agreements.filter((_, i) => i !== index));
  };

  const handleAgreementChange = (index: number, value: string) => {
    const updated = [...agreements];
    updated[index] = { description: value };
    setAgreements(updated);

    if (errors[`agreement_${index}`]) {
      const newErrors = { ...errors };
      delete newErrors[`agreement_${index}`];
      setErrors(newErrors);
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!employeeId) {
      newErrors.employeeId = 'Selecione um colaborador';
    }
    if (!date) {
      newErrors.date = 'Data é obrigatória';
    }
    if (!highlights.trim()) {
      newErrors.highlights = 'Campo obrigatório';
    }

    agreements.forEach((agreement, index) => {
      if (!agreement.description.trim()) {
        newErrors[`agreement_${index}`] = 'Descrição é obrigatória';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    const data: CreateCheckInInput = {
      employeeId,
      date,
      month,
      year,
      moodScore,
      highlights,
      concerns: concerns || undefined,
      supportNeeds: supportNeeds || undefined,
      pdiProgress,
      observations: observations || undefined,
      agreements: agreements.length > 0
        ? agreements.map((a) => ({ description: a.description }))
        : [],
    };

    await createCheckIn.mutateAsync(data);
    onSuccess?.();
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="outline" size="sm" onClick={onCancel}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar
        </Button>
        <div>
          <h2 className="text-2xl font-bold">Registrar Check-in Mensal</h2>
          <p className="text-muted-foreground">Preencha os detalhes da conversa 1:1</p>
        </div>
      </div>

      {/* Formulário */}
      <Card>
        <CardHeader>
          <CardTitle>Informações Básicas</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Colaborador e Data */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Colaborador"
              value={employeeId}
              onChange={(e) => {
                setEmployeeId(e.target.value);
                if (errors.employeeId) {
                  const newErrors = { ...errors };
                  delete newErrors.employeeId;
                  setErrors(newErrors);
                }
              }}
              error={errors.employeeId}
              options={[
                { value: '', label: 'Selecione um colaborador' },
                ...(employees?.map((emp) => ({
                  value: emp.id,
                  label: emp.user?.name || 'Sem nome',
                })) || []),
              ]}
            />

            <Input
              label="Data do Check-in"
              type="date"
              value={date}
              onChange={(e) => {
                setDate(e.target.value);
                if (errors.date) {
                  const newErrors = { ...errors };
                  delete newErrors.date;
                  setErrors(newErrors);
                }
              }}
              error={errors.date}
            />
          </div>
        </CardContent>
      </Card>

      {/* PDI Ativo do Colaborador */}
      {employeeId && activePDI && (
        <Card className="border-primary/20 bg-primary/5 dark:bg-primary/10">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Target className="h-5 w-5 text-primary" />
              PDI Ativo - Q{activePDI.quarter}/{activePDI.year}
              <Badge variant="default" className="ml-2">
                {activePDI.objectives.length} objetivo{activePDI.objectives.length > 1 ? 's' : ''}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {activePDI.objectives.map((obj, index) => (
                <div key={obj.id} className="border-l-4 border-primary pl-3 py-2 bg-background/50 rounded-r">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-xs font-semibold text-muted-foreground">
                      #{index + 1}
                    </span>
                    <h4 className="font-medium text-sm">{obj.title}</h4>
                    <Badge variant="outline" className="text-xs">
                      {obj.status === 'COMPLETED' ? '✓ Concluído' :
                       obj.status === 'IN_PROGRESS' ? '→ Em Progresso' :
                       '○ Não Iniciado'}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    <strong>Ação:</strong> {obj.action}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    <strong>Prazo:</strong> {new Date(obj.deadline).toLocaleDateString('pt-BR')}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {employeeId && !activePDI && (
        <Card className="border-yellow-500/30 bg-yellow-50 dark:bg-yellow-950/20">
          <CardContent className="pt-6 pb-6 text-center">
            <p className="text-sm text-muted-foreground">
              ⚠️ Este colaborador não possui PDI ativo no momento
            </p>
          </CardContent>
        </Card>
      )}

      {/* Avaliação */}
      <Card>
        <CardHeader>
          <CardTitle>Avaliação do Mês</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Humor */}
          <MoodSelector
            label="Como está o humor/clima do colaborador?"
            value={moodScore}
            onChange={setMoodScore}
          />

          {/* Progresso no PDI */}
          <div className="pt-4 border-t">
            <MoodSelector
              label="Progresso no PDI este mês (1-5)"
              value={pdiProgress}
              onChange={setPdiProgress}
            />
          </div>
        </CardContent>
      </Card>

      {/* Feedback da Conversa */}
      <Card>
        <CardHeader>
          <CardTitle>Feedback da Conversa</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Destaques Positivos */}
          <Textarea
            label="✨ Destaques Positivos"
            value={highlights}
            onChange={(e) => {
              setHighlights(e.target.value);
              if (errors.highlights) {
                const newErrors = { ...errors };
                delete newErrors.highlights;
                setErrors(newErrors);
              }
            }}
            error={errors.highlights}
            placeholder="O que o colaborador fez de destaque este mês?"
            rows={3}
          />

          {/* Pontos de Atenção */}
          <Textarea
            label="⚠️ Pontos de Atenção (opcional)"
            value={concerns}
            onChange={(e) => setConcerns(e.target.value)}
            placeholder="Dificuldades, bloqueios ou áreas que precisam de melhoria..."
            rows={3}
          />

          {/* Pedidos de Apoio */}
          <Textarea
            label="🤝 Pedidos de Apoio do Colaborador (opcional)"
            value={supportNeeds}
            onChange={(e) => setSupportNeeds(e.target.value)}
            placeholder="O que o colaborador precisa da empresa/gestor?"
            rows={2}
          />

          {/* Observações */}
          <Textarea
            label="💭 Observações (opcional)"
            value={observations}
            onChange={(e) => setObservations(e.target.value)}
            placeholder="Anotações adicionais sobre o check-in..."
            rows={2}
          />
        </CardContent>
      </Card>

      {/* Acordos/Próximos Passos */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>📋 Acordos/Próximos Passos</CardTitle>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleAddAgreement}
            >
              <Plus className="h-4 w-4 mr-2" />
              Adicionar Acordo
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {agreements.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              Nenhum acordo adicionado. Clique em "Adicionar Acordo" para criar compromisos.
            </p>
          ) : (
            agreements.map((agreement, index) => (
              <div key={index} className="flex gap-2">
                <Input
                  value={agreement.description}
                  onChange={(e) => handleAgreementChange(index, e.target.value)}
                  error={errors[`agreement_${index}`]}
                  placeholder={`Acordo ${index + 1}`}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleRemoveAgreement(index)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      {/* Ações */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancelar
            </Button>
            <Button onClick={handleSubmit} disabled={createCheckIn.isPending}>
              {createCheckIn.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <Calendar className="h-4 w-4 mr-2" />
                  Registrar Check-in
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
